// Browser-owned install events are one-use capabilities, not installation proof.
export function createAppInstallController(browser) {
  let media;
  let started = false;
  let pending = null;
  let request = 0;
  let observedInstallation = false;
  const listeners = new Set();
  const standalone = () => Boolean(
    media?.matches || browser?.navigator?.standalone === true
  );
  let snapshot = { installed: false, standalone: false, canPrompt: false, prompting: false, message: "" };
  const update = (patch) => {
    snapshot = { ...snapshot, ...patch };
    listeners.forEach((listener) => listener());
  };
  const refreshDisplay = () => {
    const mode = standalone();
    if (mode || observedInstallation) pending = null;
    update({ standalone: mode, installed: mode || observedInstallation,
      canPrompt: Boolean(pending) && !mode && !observedInstallation });
  };
  const onPrompt = (event) => {
    if (typeof event.prompt !== "function" || snapshot.installed) return;
    event.preventDefault();
    if (snapshot.prompting) return;
    pending = event;
    update({ canPrompt: true, message: "" });
  };
  const onInstalled = () => {
    observedInstallation = true;
    pending = null;
    update({ installed: true, canPrompt: false, message: "Competition Checker is installed. Open it from your home screen." });
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start() {
      if (started || !browser?.addEventListener) return;
      started = true;
      media = browser.matchMedia?.("(display-mode: standalone)");
      refreshDisplay();
      browser.addEventListener("beforeinstallprompt", onPrompt);
      browser.addEventListener("appinstalled", onInstalled);
      if (media?.addEventListener) media.addEventListener("change", refreshDisplay);
      else media?.addListener?.(refreshDisplay);
    },
    stop() {
      if (!started) return;
      started = false;
      request += 1;
      pending = null;
      browser.removeEventListener("beforeinstallprompt", onPrompt);
      browser.removeEventListener("appinstalled", onInstalled);
      if (media?.removeEventListener) media.removeEventListener("change", refreshDisplay);
      else media?.removeListener?.(refreshDisplay);
      update({ canPrompt: false, prompting: false });
    },
    async promptInstall() {
      if (!started || snapshot.installed || snapshot.prompting || !pending) return;
      const event = pending;
      pending = null;
      const currentRequest = ++request;
      update({ canPrompt: false, prompting: true, message: "" });
      try {
        // Invoke synchronously from the user's click, before the first await.
        const result = await event.prompt();
        const choice = result?.outcome ? result : await event.userChoice;
        if (!started || currentRequest !== request || snapshot.installed) return;
        update({ message: choice?.outcome === "accepted"
          ? "Install request accepted. Follow your browser's instructions to finish."
          : "Installation was not completed. You can use your browser menu to try later." });
      } catch {
        if (started && currentRequest === request && !snapshot.installed) {
          update({ message: "The install prompt is unavailable. Try the browser menu instructions below." });
        }
      } finally {
        if (started && currentRequest === request) update({ prompting: false });
      }
    },
  };
}
